import React from "react";
import {
  addDays,
  addMonths,
  differenceInCalendarMonths,
  endOfMonth,
  startOfMonth,
} from "date-fns";
import { useHistory } from "react-router-dom";
import { useMerchantAuthUser } from "../auth/MerchantAuthContext";
import MerchantDateRangePickerField from "../components/MerchantDateRangePickerField";
import MerchantStatusBadge from "../components/MerchantStatusBadge";
import { MerchantDownloadIcon } from "../components/MerchantIcons";
import {
  downloadMerchantBlob,
  exportMerchantTransactions,
  fetchMerchantTransactionFilters,
  fetchMerchantTransactions,
  getMerchantApiErrorMessage,
  normalizeMerchantApiTransaction,
} from "../data/merchantApi";
import {
  getMerchantCountryCode,
  formatMerchantPaymentMethod,
  getMerchantAuthBusinessId,
  getMerchantMethod,
  getMerchantStatus,
  getMerchantStatusLabel,
  pickMerchantField,
  toMerchantString,
} from "../data/merchantLegacyUtils";
import { formatMerchantCurrency } from "../data/merchantData";

const PAGE_SIZE = 20;
const MAX_TRANSACTION_RANGE_MONTHS = 3;

function formatOutletDisplayName(name) {
  return toMerchantString(name).replace("@", " · ");
}

function getCurrentMerchantMonth() {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");

  return String(now.getFullYear()) + "-" + month;
}

function createInitialQuickFilters() {
  const currentMonthDate = getMonthDate(getCurrentMerchantMonth()) || new Date();

  return {
    storeId: "",
    method: "all",
    paymentStatus: "",
    periodStart: formatQueryDate(startOfMonth(currentMonthDate)),
    periodEnd: formatQueryDate(endOfMonth(currentMonthDate)),
  };
}

function createEmptyFilterOptions() {
  return {
    outlets: [],
    paymentMethods: [],
    statuses: [],
    months: [],
  };
}

function normalizeOutletId(storeId) {
  const normalizedStoreId = toMerchantString(storeId).trim();

  return normalizedStoreId || undefined;
}

function getMonthDate(monthValue) {
  const normalizedMonth = toMerchantString(monthValue);
  const matched = normalizedMonth.match(/^(\d{4})-(\d{2})$/);

  if (!matched) {
    return null;
  }

  return new Date(Number(matched[1]), Number(matched[2]) - 1, 1);
}

function getMonthValue(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return "";
  }

  return (
    String(date.getFullYear()) +
    "-" +
    String(date.getMonth() + 1).padStart(2, "0")
  );
}

function getDateValue(dateValue) {
  const normalizedDate = toMerchantString(dateValue);
  const matched = normalizedDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);

  if (!matched) {
    return null;
  }

  return new Date(Number(matched[1]), Number(matched[2]) - 1, Number(matched[3]));
}

function buildTransactionMonths(filters) {
  const startMonth = getDateValue(filters.periodStart);
  const endMonth = getDateValue(filters.periodEnd || filters.periodStart);

  if (!startMonth || !endMonth) {
    return [];
  }

  const normalizedStart = startOfMonth(startMonth);
  const normalizedEnd = startOfMonth(endMonth);
  const firstMonth =
    normalizedStart.getTime() <= normalizedEnd.getTime() ? normalizedStart : normalizedEnd;
  const lastMonth =
    normalizedStart.getTime() <= normalizedEnd.getTime() ? normalizedEnd : normalizedStart;
  const rangeLength =
    differenceInCalendarMonths(lastMonth, firstMonth) + 1;
  const safeRangeLength = Math.max(
    1,
    Math.min(MAX_TRANSACTION_RANGE_MONTHS, rangeLength),
  );
  const monthValues = [];

  for (let index = 0; index < safeRangeLength; index += 1) {
    monthValues.push(getMonthValue(addMonths(firstMonth, index)));
  }

  return monthValues;
}

function formatQueryDate(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return "";
  }

  return (
    String(date.getFullYear()) +
    "-" +
    String(date.getMonth() + 1).padStart(2, "0") +
    "-" +
    String(date.getDate()).padStart(2, "0")
  );
}

function buildTransactionDateRange(filters) {
  const startDate = getDateValue(filters.periodStart);
  const endDate = getDateValue(filters.periodEnd || filters.periodStart);

  if (!startDate && !endDate) {
    return {
      start_date: undefined,
      end_date: undefined,
    };
  }

  const normalizedStart = startDate || endDate;
  const normalizedEnd = endDate || startDate;
  const firstDate =
    normalizedStart.getTime() <= normalizedEnd.getTime() ? normalizedStart : normalizedEnd;
  const lastDate =
    normalizedStart.getTime() <= normalizedEnd.getTime() ? normalizedEnd : normalizedStart;

  return {
    start_date: formatQueryDate(firstDate),
    end_date: formatQueryDate(lastDate),
  };
}

function buildBaseTransactionQueryParams(filters) {
  return {
    ...buildTransactionDateRange(filters),
    outlet_id: normalizeOutletId(filters.storeId),
    payment_method: filters.method !== "all" ? filters.method : undefined,
    status: filters.paymentStatus || undefined,
  };
}

function buildTransactionQueryParams(filters, page) {
  return {
    ...buildBaseTransactionQueryParams(filters),
    page: page,
    page_size: PAGE_SIZE,
  };
}

function formatMonthLabel(monthValue) {
  const normalizedMonth = toMerchantString(monthValue);
  const matched = normalizedMonth.match(/^(\d{4})-(\d{2})$/);

  if (!matched) {
    return normalizedMonth || "All Periods";
  }

  const monthDate = new Date(Date.UTC(Number(matched[1]), Number(matched[2]) - 1, 1));

  return monthDate.toLocaleString("en-SG", {
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

function formatMonthRangeLabel(startMonthValue, endMonthValue) {
  const startDate = getDateValue(startMonthValue);
  const endDate = getDateValue(endMonthValue || startMonthValue);

  if (startDate || endDate) {
    const startLabel = (startDate || endDate).toLocaleDateString("en-SG", {
      month: "short",
      day: "2-digit",
      year: "numeric",
    });
    const endLabel = (endDate || startDate).toLocaleDateString("en-SG", {
      month: "short",
      day: "2-digit",
      year: "numeric",
    });

    return startLabel === endLabel ? startLabel : startLabel + " - " + endLabel;
  }

  const startLabel = formatMonthLabel(startMonthValue);
  const endLabel = formatMonthLabel(endMonthValue || startMonthValue);

  return startLabel === endLabel ? startLabel : startLabel + " - " + endLabel;
}

function normalizeFilterOptions(response) {
  return {
    outlets: Array.isArray(response && response.outlets) ? response.outlets : [],
    paymentMethods: Array.isArray(response && response.payment_methods)
      ? response.payment_methods
          .map(function mapMethod(method) {
            return toMerchantString(method);
          })
          .filter(Boolean)
      : [],
    statuses: Array.isArray(response && response.statuses)
      ? response.statuses
          .map(function mapStatus(option) {
            const value = toMerchantString(option && option.value);
            const label = toMerchantString(option && option.label, value);

            return value ? { value: value, label: label } : null;
          })
          .filter(Boolean)
      : [],
    months: Array.isArray(response && response.months)
      ? response.months
          .map(function mapMonth(month) {
            return toMerchantString(month);
          })
          .filter(Boolean)
      : [],
  };
}

function getFallbackOutletOptions(stores) {
  return stores
    .map(function mapStore(store) {
      const value = toMerchantString(pickMerchantField(store, ["id"]), "");
      const label = toMerchantString(
        pickMerchantField(store, ["name", "merchantName", "merchant_name"]),
        value,
      );

      return value ? { value: value, label: label } : null;
    })
    .filter(Boolean);
}

export default function MerchantTransactionsPage() {
  const history = useHistory();
  const authUser = useMerchantAuthUser();
  const merchantBusinessId = getMerchantAuthBusinessId(authUser);
  const merchantBusinessName = toMerchantString(
    pickMerchantField(authUser, ["businessName", "name"]),
    "Merchant",
  );
  const stores = React.useMemo(
    function getStores() {
      return Array.isArray(authUser && authUser.stores) ? authUser.stores : [];
    },
    [authUser],
  );
  const [page, setPage] = React.useState(1);
  const [quickFilters, setQuickFilters] = React.useState(createInitialQuickFilters);
  const [appliedQuickFilters, setAppliedQuickFilters] = React.useState(createInitialQuickFilters);
  const [feedbackMessage, setFeedbackMessage] = React.useState("");
  const [filtersFeedbackMessage, setFiltersFeedbackMessage] = React.useState("");
  const [isExporting, setIsExporting] = React.useState(false);
  const [requestState, setRequestState] = React.useState("loading");
  const [filterOptions, setFilterOptions] = React.useState(createEmptyFilterOptions);
  const [remoteTransactions, setRemoteTransactions] = React.useState([]);
  const [remoteTotal, setRemoteTotal] = React.useState(0);
  const hasPendingFilterChanges =
    JSON.stringify(quickFilters) !== JSON.stringify(appliedQuickFilters);
  const selectedMonths = React.useMemo(
    function createSelectedMonths() {
      return buildTransactionMonths(appliedQuickFilters);
    },
    [appliedQuickFilters],
  );

  const outletOptions = React.useMemo(
    function buildOutletOptions() {
      if (filterOptions.outlets.length) {
        return filterOptions.outlets
          .map(function mapOutlet(outlet) {
            const value = toMerchantString(outlet && outlet.id, "");
            const label = toMerchantString(outlet && outlet.name, value);

            return value ? { value: value, label: label } : null;
          })
          .filter(Boolean);
      }

      return getFallbackOutletOptions(stores);
    },
    [filterOptions.outlets, stores],
  );

  const methodOptions = React.useMemo(
    function buildMethodOptions() {
      return [{ value: "all", label: "All Methods" }].concat(
        filterOptions.paymentMethods.map(function mapMethod(method) {
          return {
            value: method,
            label: formatMerchantPaymentMethod(method),
          };
        }),
      );
    },
    [filterOptions.paymentMethods],
  );

  const statusOptions = React.useMemo(
    function buildStatusOptions() {
      return [{ value: "", label: "All Statuses" }].concat(filterOptions.statuses);
    },
    [filterOptions.statuses],
  );

  const monthBounds = React.useMemo(
    function createMonthBounds() {
      const monthValues = filterOptions.months.length
        ? filterOptions.months
        : selectedMonths;
      const monthDates = monthValues
        .map(function mapMonth(monthValue) {
          return getMonthDate(monthValue);
        })
        .filter(Boolean)
        .sort(function sortMonthDates(firstDate, secondDate) {
          return firstDate.getTime() - secondDate.getTime();
        });

      return {
        minDate: monthDates.length ? monthDates[0] : null,
        maxDate: monthDates.length ? endOfMonth(monthDates[monthDates.length - 1]) : null,
      };
    },
    [filterOptions.months, selectedMonths],
  );

  React.useEffect(
    function loadFilterOptions() {
      let isActive = true;

      async function requestFilterOptions() {
        try {
          const response = await fetchMerchantTransactionFilters(
            buildTransactionDateRange(appliedQuickFilters),
          );

          if (!isActive) {
            return;
          }

          setFilterOptions(normalizeFilterOptions(response));
          setFiltersFeedbackMessage("");
        } catch (error) {
          if (!isActive) {
            return;
          }

          setFilterOptions(createEmptyFilterOptions());
          setFiltersFeedbackMessage(
            [
              "Transaction filter options are not fully available from the backend yet.",
              "The page is falling back to broad selectors only.",
              getMerchantApiErrorMessage(error, ""),
            ]
              .filter(Boolean)
              .join(" "),
          );
        }
      }

      requestFilterOptions();

      return function cleanup() {
        isActive = false;
      };
    },
    [appliedQuickFilters.periodEnd, appliedQuickFilters.periodStart],
  );

  React.useEffect(
    function loadTransactions() {
      let isActive = true;

      async function requestTransactions() {
        setRequestState("loading");
        setFeedbackMessage("");

        try {
          const response = await fetchMerchantTransactions(
            buildTransactionQueryParams(appliedQuickFilters, page),
          );

          if (!isActive) {
            return;
          }

          setRemoteTransactions(
            Array.isArray(response.items)
              ? response.items.map(normalizeMerchantApiTransaction)
              : [],
          );
          setRemoteTotal(Number(response.total || 0));

          setRequestState("success");
          setFeedbackMessage("");
        } catch (error) {
          if (!isActive) {
            return;
          }

          setRemoteTransactions([]);
          setRemoteTotal(0);
          setRequestState("error");
          setFeedbackMessage(
            [
              "Merchant transactions request failed. No preview data is injected; the table stays empty until the backend responds.",
              getMerchantApiErrorMessage(error, ""),
            ]
              .filter(Boolean)
              .join(" "),
          );
        }
      }

      requestTransactions();

      return function cleanup() {
        isActive = false;
      };
    },
    [appliedQuickFilters, page],
  );

  function handleQuickFilterChange(event) {
    const fieldName = event.target.name;
    const nextValue = event.target.value;

    setQuickFilters(function updateQuickFilters(previousState) {
      return {
        ...previousState,
        [fieldName]: nextValue,
      };
    });
  }

  function handlePeriodRangeChange(dates) {
    const startDate = dates && dates[0] ? dates[0] : null;
    const endDate = dates && dates[1] ? dates[1] : null;

    if (!startDate) {
      return;
    }

    if (!endDate) {
      setQuickFilters(function updatePeriodRange(previousState) {
        return {
          ...previousState,
          periodStart: formatQueryDate(startDate),
          periodEnd: "",
        };
      });
      return;
    }

    const normalizedStartDate =
      startDate.getTime() <= endDate.getTime() ? startDate : endDate;
    let nextEndDate =
      startDate.getTime() <= endDate.getTime() ? endDate : startDate;

    if (
      differenceInCalendarMonths(nextEndDate, normalizedStartDate) >=
      MAX_TRANSACTION_RANGE_MONTHS
    ) {
      nextEndDate = addDays(addMonths(normalizedStartDate, MAX_TRANSACTION_RANGE_MONTHS), -1);
      setFeedbackMessage(
        "Transactions search supports up to 3 months. The selected range was capped automatically.",
      );
    } else if (!requestState || requestState === "success") {
      setFeedbackMessage("");
    }

    setQuickFilters(function updatePeriodRange(previousState) {
      return {
        ...previousState,
        periodStart: formatQueryDate(normalizedStartDate),
        periodEnd: formatQueryDate(nextEndDate),
      };
    });
  }

  function applyFilters() {
    setAppliedQuickFilters({ ...quickFilters });
    setPage(1);
  }

  function handleResetFilters() {
    const nextQuickFilters = createInitialQuickFilters();

    setQuickFilters(nextQuickFilters);
    setAppliedQuickFilters(nextQuickFilters);
    setPage(1);
    setFeedbackMessage("");
    setFiltersFeedbackMessage("");
  }

  async function handleExport() {
    setIsExporting(true);
    setFeedbackMessage("Preparing transactions CSV from the merchant export API...");

    try {
      const exportResult = await exportMerchantTransactions(
        buildBaseTransactionQueryParams(appliedQuickFilters),
      );

      downloadMerchantBlob(exportResult.blob, exportResult.filename);
      setFeedbackMessage("Transactions CSV exported from the merchant export API.");
    } catch (error) {
      setFeedbackMessage(
        [
          "Transactions export failed.",
          getMerchantApiErrorMessage(error, ""),
        ]
          .filter(Boolean)
          .join(" "),
      );
    } finally {
      setIsExporting(false);
    }
  }

  return (
    <div className="merchant-page">
      <div className="merchant-page-header">
        <div className="merchant-page-title">Transactions</div>
        <div className="merchant-page-subtitle">
          All transactions · {merchantBusinessName} ·{" "}
          {formatMonthRangeLabel(
            appliedQuickFilters.periodStart,
            appliedQuickFilters.periodEnd,
          )}
        </div>
      </div>

      {requestState === "loading" ||
      requestState === "error" ||
      feedbackMessage ||
      filtersFeedbackMessage ? (
        <div className="merchant-data-note">
          {requestState === "loading"
            ? "Loading live transactions for the selected period."
            : feedbackMessage || filtersFeedbackMessage}
          {merchantBusinessId ? (
            <div className="merchant-inline-status">Business scope {merchantBusinessId}</div>
          ) : null}
          {requestState === "loading" ? (
            <div className="merchant-inline-status merchant-inline-status-loading">
              <span className="merchant-request-spinner" />
              Transactions request in progress
            </div>
          ) : null}
          {requestState === "error" ? (
            <div className="merchant-inline-status merchant-inline-status-error">
              Request failed
            </div>
          ) : null}
          {filtersFeedbackMessage ? (
            <div className="merchant-inline-status merchant-inline-status-error">
              {filtersFeedbackMessage}
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="merchant-filters-bar">
        <select
          className="merchant-select"
          name="storeId"
          value={quickFilters.storeId}
          onChange={handleQuickFilterChange}
        >
          <option value="">All Outlets</option>
          {outletOptions.map(function renderStore(option) {
            return (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            );
          })}
        </select>

        <select
          className="merchant-select"
          name="method"
          value={quickFilters.method}
          onChange={handleQuickFilterChange}
        >
          {methodOptions.map(function renderMethodOption(option) {
            return (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            );
          })}
        </select>

        <select
          className="merchant-select"
          name="paymentStatus"
          value={quickFilters.paymentStatus}
          onChange={handleQuickFilterChange}
        >
          {statusOptions.map(function renderStatusOption(option) {
            return (
              <option key={option.value || option.label} value={option.value}>
                {option.label}
              </option>
            );
          })}
        </select>

        <MerchantDateRangePickerField
          startDate={getDateValue(quickFilters.periodStart)}
          endDate={getDateValue(quickFilters.periodEnd)}
          minDate={monthBounds.minDate}
          maxDate={monthBounds.maxDate}
          placeholder="Select date range"
          rangeDisplayFormat="MM/dd"
          onChange={handlePeriodRangeChange}
        />

        <div className="merchant-spacer" />

        <button
          className="merchant-button merchant-button-primary"
          type="button"
          onClick={handleExport}
          disabled={isExporting || requestState === "loading"}
        >
          <MerchantDownloadIcon />
          {isExporting ? "Exporting..." : "Export excel"}
        </button>

        {hasPendingFilterChanges ? (
          <React.Fragment>
            <button
              className="merchant-button merchant-button-outline"
              type="button"
              onClick={handleResetFilters}
              disabled={requestState === "loading"}
            >
              Reset
            </button>

            <button
              className="merchant-button merchant-button-outline"
              type="button"
              onClick={applyFilters}
              disabled={requestState === "loading"}
            >
              Apply
            </button>
          </React.Fragment>
        ) : null}
      </div>

      <div className="merchant-card merchant-card-table">
        <div className="merchant-table-wrap">
          <table className="merchant-table">
            <thead>
              <tr>
                <th>Transaction ID</th>
                <th>Outlet</th>
                <th>Amount (SGD)</th>
                <th>Method</th>
                <th>Scheme</th>
                <th>Issuer Country</th>
                <th>Status</th>
                <th>Date &amp; Time</th>
                <th>Net</th>
              </tr>
            </thead>
            <tbody>
              {remoteTransactions.length ? (
                remoteTransactions.map(function renderRow(transaction, index) {
                  const transactionId = toMerchantString(
                    pickMerchantField(transaction, [
                      "id",
                      "txnId",
                      "tradeNumber",
                      "acquirerTransactionId",
                    ]),
                    "N/A",
                  );
                  const storeName = toMerchantString(
                    pickMerchantField(transaction, ["storeName", "store"]),
                    "-",
                  );
                  const method = getMerchantMethod(transaction);
                  const status = getMerchantStatus(transaction);

                  return (
                    <tr
                      key={transactionId + "-" + index}
                      className="merchant-clickable-row"
                      onClick={function openTransactionDetail() {
                        history.push(
                          "/transactions/" +
                            toMerchantString(pickMerchantField(transaction, ["id", "txnId"]), ""),
                        );
                      }}
                    >
                      <td className="merchant-mono" title={transactionId}>
                        {transactionId}
                      </td>
                      <td>{formatOutletDisplayName(storeName)}</td>
                      <td className="merchant-mono">
                        {formatMerchantCurrency(
                          pickMerchantField(transaction, ["amount"], 0),
                          2,
                        )}
                      </td>
                      <td>
                        <span className="merchant-table-chip">{method}</span>
                      </td>
                      <td>{pickMerchantField(transaction, ["cardPayWay", "scheme"], "-")}</td>
                      <td>{getMerchantCountryCode(transaction)}</td>
                      <td>
                        <MerchantStatusBadge
                          status={status}
                          label={getMerchantStatusLabel(status)}
                        />
                      </td>
                      <td>
                        {pickMerchantField(
                          transaction,
                          ["capturedTime", "creationTime", "createdAt"],
                          "-",
                        )}
                      </td>
                      <td className="merchant-mono">
                        {formatMerchantCurrency(
                          pickMerchantField(transaction, ["netAmount", "amount"], 0),
                          2,
                        )}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td className="merchant-table-empty" colSpan="9">
                    {requestState === "loading"
                      ? "Loading transactions..."
                      : "No transactions available yet."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="merchant-card-footer">
          <span>
            Showing {(remoteTransactions || []).length} of {remoteTotal.toLocaleString("en-SG")} transactions
          </span>
          <div className="merchant-pagination">
            <button
              className="merchant-button merchant-button-outline"
              type="button"
              disabled={page <= 1 || requestState === "loading"}
              onClick={function handlePrevPage() {
                setPage(Math.max(1, page - 1));
              }}
            >
              ← Prev
            </button>
            <button
              className="merchant-button merchant-button-outline"
              type="button"
              disabled={page * PAGE_SIZE >= remoteTotal || requestState === "loading"}
              onClick={function handleNextPage() {
                setPage(page + 1);
              }}
            >
              Next →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
